import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { VortexService } from '../services/vortexService';
import { Activity } from 'lucide-react';

export const DendroVisualizer: React.FC = () => {
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    VortexService.getDendroData().then(setData);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-display font-bold">Dendro-Drift Analysis</h3>
          <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest mt-1">
            Physical Record (C14) vs. Linear Narrative Drift
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-lattice-cyan" />
            <span className="text-[8px] font-mono text-white/40 uppercase">Physical Truth</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white/20" />
            <span className="text-[8px] font-mono text-white/40 uppercase">Muffled Narrative</span>
          </div>
        </div>
      </div>

      <div className="h-[300px] w-full glass-panel p-4 bg-black/40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorPhys" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00ffd5" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#00ffd5" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
            <XAxis 
              dataKey="year" 
              stroke="#ffffff20" 
              fontSize={8} 
              fontFamily="monospace"
              tickFormatter={(val) => `${Math.abs(val)} BP`}
            />
            <YAxis stroke="#ffffff20" fontSize={8} fontFamily="monospace" hide />
            <Tooltip 
              contentStyle={{ backgroundColor: '#000', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
              itemStyle={{ fontSize: '10px', fontFamily: 'monospace' }}
              labelStyle={{ fontSize: '10px', fontFamily: 'monospace', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}
              labelFormatter={(val) => `${Math.abs(val)} BP`}
            />
            <Area 
              type="monotone" 
              dataKey="physical" 
              stroke="#00ffd5" 
              fillOpacity={1} 
              fill="url(#colorPhys)" 
              strokeWidth={2}
            />
            <Area 
              type="monotone" 
              dataKey="narrative" 
              stroke="rgba(255,255,255,0.2)" 
              fill="transparent" 
              strokeWidth={1}
              strokeDasharray="5 5"
            />
            <ReferenceLine 
              x={-14350} 
              stroke="#00ffd5" 
              strokeDasharray="3 3" 
              label={{ position: 'top', value: '14350 BP ANCHOR', fill: '#00ffd5', fontSize: 8, fontFamily: 'monospace' }} 
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/10">
        <div className="flex gap-4 items-start">
          <Activity className="w-5 h-5 text-lattice-cyan shrink-0 mt-1" />
          <div className="space-y-1">
            <span className="text-[10px] font-mono text-lattice-cyan uppercase font-bold">Derivation Result:</span>
            <p className="text-[11px] text-white/60 leading-relaxed italic">
              "The 14350 BP pulse reveals a structural tension that linear history cannot resolve. 
              The 'drift' identified at the anchor point is not an anomaly—it is the physical proof 
              of the 1x1=2 expansion engine bypassing the 1.0 noise."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
